import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save } from 'lucide-react';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import { formatRoleName } from '../../utils/roleUtils';
import SiteRequiredGate from '../../components/SiteRequiredGate';
import AIPrefillPanel from '../../components/assistant/AIPrefillPanel';
import SimilarRecordsPanel from '../../components/assistant/SimilarRecordsPanel';
import AIDraftWarningBanner, { AIVerifyAcknowledgement } from '../../components/assistant/AIDraftWarningBanner';
import useAIDraftForm from '../../hooks/useAIDraftForm';
import useSimilarRecords from '../../hooks/useSimilarRecords';
import * as capaDraftMapper from '../../utils/aiPrefill/capaDraftMapper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAPACreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSiteId, activeSiteName } = useSite();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const prefill = (() => {
    const p = new URLSearchParams(location.search);
    return {
      title: p.get('title') || '',
      description: p.get('description') || '',
      source: p.get('source') || 'manual',
      source_id: p.get('source_id') || '',
      severity: p.get('severity') || 'Medium',
    };
  })();

  const [formData, setFormData] = useState({
    title: prefill.title,
    description: prefill.description,
    capa_type: 'corrective',
    severity: prefill.severity,
    priority: 'medium',
    source: prefill.source,
    source_id: prefill.source_id,
    // QA #87d: assigned_to is the real user id. This form used to post the
    // literal 'temp-user-id' and only a free-text name, so every standalone
    // CAPA was filed against a user that does not exist and its assignment
    // notification went to nobody.
    assigned_to: '',
    assigned_to_name: '',
    // QA #87a: the HIRA CAPA form has always had these two; this form did not,
    // so the two CAPA creation surfaces disagreed on what a CAPA even records.
    corrective_actions: '',
    preventive_actions: '',
    due_date: '',
    root_cause: '',
    department_id: '',
    site_id: '',
    site_name: ''
  });
  const [departments, setDepartments] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  // Assignee options. Uses the existing least-privilege directory endpoint
  // (GET /api/users/directory) rather than /api/users: it returns id/name/role
  // only and needs no users:read grant, so roles that legitimately raise a CAPA
  // can pick an owner without being able to see user-management data.
  // Scoped to the active site so the picker never widens past the caller's
  // site scope, and never becomes a tenant-wide user-enumeration surface.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const url = activeSiteId
      ? `${API}/users/directory?site_id=${encodeURIComponent(activeSiteId)}`
      : `${API}/users/directory`;
    axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAssignableUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAssignableUsers([]));
  }, [activeSiteId]);

  // AI prefill: shared draft state (mandatory warning banner, fill-empty-only
  // merge, chat handoff consumption, feedback beacon). The legacy
  // URLSearchParams prefill above stays untouched — fields it filled are
  // simply reported as "already filled" by the AI apply path.
  //
  // assignableUsers is in the context so the mapper can resolve an AI-suggested
  // assignee NAME to the user id the Assigned To select is bound to; without it
  // an AI-filled assignee would set nothing the user can see.
  const ai = useAIDraftForm({
    module: 'capa',
    mapper: capaDraftMapper,
    formData,
    setFormData,
    getContext: () => ({ departments, assignableUsers }),
  });
  const { suggestions: similarCapas, loading: similarLoading } = useSimilarRecords(
    'capa', `${formData.title} ${formData.description}`.trim()
  );

  // Auto-set site from active site context (global site switcher)
  useEffect(() => {
    if (activeSiteId) {
      setFormData(prev => ({ ...prev, site_id: activeSiteId, site_name: activeSiteName || '' }));
    } else {
      setFormData(prev => ({ ...prev, site_id: '', site_name: '' }));
    }
  }, [activeSiteId, activeSiteName]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.site_id) {
      setError('Please select a site from the header first.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        department_id: formData.department_id || null,
      };

      await axios.post(`${API}/capa`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Evaluation beacon (field names only; failures swallowed).
      ai.sendFeedback(formData);

      setSuccess(true);
      setTimeout(() => {
        navigate('/capa');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create CAPA');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-green-600 text-5xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">CAPA Created!</h2>
            <p className="text-gray-600">Redirecting to CAPA list...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <Button variant="outline" onClick={() => navigate('/capa')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to CAPA List
      </Button>

      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Create New CAPA</CardTitle>
            <p className="text-gray-600 text-sm">Corrective and Preventive Action</p>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* QA #97: a CAPA is site-scoped (routes/capa.py raises "No active
                site selected..."), so gate the form rather than let it be filled
                in and rejected on submit. */}
            <SiteRequiredGate entityLabel="CAPA">
            {/* AI prefill sits INSIDE the gate: a draft is only useful once
                there is a site to file it against, and the gate already
                withholds everything below until one is selected. The panel's
                own `disabled` guard is kept as a fallback rather than relied on. */}
            {/* MANDATORY AI warning — shown every time data is pre-populated */}
            <div className="mb-4">
              <AIDraftWarningBanner
                aiFieldCount={ai.aiFields.size}
                lowCertaintyFields={ai.lowCertaintyFields}
                appliedAt={ai.aiMeta?.appliedAt}
              />
            </div>

            <AIPrefillPanel
              module="capa"
              onDraft={ai.applyAIDraft}
              disabled={!activeSiteId}
              disabledHint="Select a site from the header first"
              placeholder="Describe the problem or finding, e.g. 'Guard missing on conveyor C-3, found during weekly inspection, needs fixing by month end'"
            />


            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Site (auto-set from header site switcher) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Site <span className="text-red-600">*</span>
                </label>
                {activeSiteId ? (
                  <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                    {activeSiteName || 'Loading...'}
                  </div>
                ) : (
                  <div className="w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
                    Please select a site from the header first.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the CAPA"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-600">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500${ai.aiClass('description')}`}
                  placeholder="Detailed description of the corrective/preventive action"
                />
                <SimilarRecordsPanel
                  suggestions={similarCapas}
                  loading={similarLoading}
                  title="similar past CAPAs"
                  onStartFrom={(rec) =>
                    ai.applyAIDraft({ draft: capaDraftMapper.draftFromSimilarRecord(rec) })}
                />
              </div>

              {/* QA #87a: parity with the HIRA CAPA form, which has offered these
                  two separately all along. Stored in their own columns
                  (capas.corrective_actions / preventive_actions), so they survive
                  the CAPA lifecycle rather than being fused into Description
                  (QA #88). */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Corrective Action
                </label>
                <textarea
                  name="corrective_actions"
                  value={formData.corrective_actions}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Action to address the problem that occurred"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preventive Action
                </label>
                <textarea
                  name="preventive_actions"
                  value={formData.preventive_actions}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Action to stop it recurring"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    CAPA Type <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="capa_type"
                    value={formData.capa_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="corrective">Corrective</option>
                    <option value="preventive">Preventive</option>
                    <option value="immediate">Immediate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Severity <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="severity"
                    value={formData.severity}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Priority <span className="text-red-600">*</span>
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Due Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split("T")[0]}
                    max="2099-12-31"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Assigned To <span className="text-red-600">*</span>
                </label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={(e) => {
                    const picked = assignableUsers.find(u => u.id === e.target.value);
                    setFormData({
                      ...formData,
                      assigned_to: e.target.value,
                      // Kept in step with the id so the stored display name can
                      // never disagree with the assignee. The server re-reads the
                      // name from the users table regardless.
                      assigned_to_name: picked ? picked.name : '',
                    });
                  }}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Select a person —</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name}{u.role ? ` (${formatRoleName(u.role)})` : ''}
                    </option>
                  ))}
                </select>
                {assignableUsers.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">
                    No assignable users found for this site. Ask an administrator to
                    assign users to it.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Root Cause (Optional)
                </label>
                <textarea
                  name="root_cause"
                  value={formData.root_cause}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Identified root cause requiring this CAPA"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Select department —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              {/* Mandatory AI verification acknowledgement — blocks submit
                  until ticked whenever AI populated any field this session. */}
              {ai.requiresAck && (
                <AIVerifyAcknowledgement
                  checked={ai.aiVerifiedAck}
                  onChange={ai.setAiVerifiedAck}
                  unreviewedCount={ai.aiFields.size}
                />
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={loading || !formData.site_id || (ai.requiresAck && !ai.aiVerifiedAck)}
                  title={ai.requiresAck && !ai.aiVerifiedAck ? 'Confirm you have verified all AI-generated fields first' : undefined}
                  className="flex-1"
                >
                  {loading ? (
                    <>Loading...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create CAPA
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/capa')}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </form>
            </SiteRequiredGate>
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
};

export default CAPACreate;
